"use client";
import { Track } from "@/src/lib/tracking";

import { useCallback, useEffect, useMemo } from "react";
import { OspToast } from "@ospweb/sdk-ui";
import { cn, safeStringifyError } from "utils";
import {
  trackGiftGoalSetupDone,
  trackPutEdToWorkTap,
} from "@/src/hooks/onboarding/onboardingAnalytics";
import { getOnboardingV3Session } from "@/src/hooks/onboarding/onboardingV3SessionStorage";
import { resolveGoalIdForActivate } from "@/src/hooks/goal/resolveGoalIdForActivate";
import { trackPlanSetGoalDone } from "@screens/plan/planActivityAnalytics";
import { SaveGoalCardGoalSection } from "./SaveGoalCardGoalPreview";
import { SaveGoalCardPlanPreviewSection } from "./SaveGoalCardPlanPreviewSection";
import {
  hasSaveGoalResult,
  SAVE_GOAL_ACTIVATED_TOOL_MESSAGE,
} from "./parseSaveGoalResult";
import type {
  InteractiveToolInfo,
  SaveGoalArgs,
  SaveGoalCardSubmitPayload,
} from "./types";
import { useMemoizedFn } from "ahooks";
import { normalizeSaveGoalTrackerSpecs } from "./saveGoalTrackerSpecs";
import { SAVE_GOAL_ACTIVATE_COPY } from "./saveGoalActivateCardCopy";
import {
  closePlanUnlockedMovesModal,
  openPlanUnlockedMovesModal,
} from "./PlanUnlockedMovesModal";
import { OnboardingEmphasisCtaButton } from "@components/OnboardingWizard/shared/OnboardingEmphasisCtaButton";
import { useGoalActivatePaywallGate } from "@/src/hooks/goal/useGoalActivatePaywallGate";
import { GoalConfirmScoreImpact } from "./GoalConfirmScoreImpact";
import { useFitnessScore } from "@/src/hooks/fitness";
import { trackGoalCreateSuccess } from "@/src/features/onboarding-checklist/utils/tracking";

/** 复用既有 i18n key 文案（translate 只读，不新增 key） */
const ACTIVATE_FAILED_TOAST = "Something went wrong. Please try again.";
const ACTIVATE_FAILED_TOAST_KEY = "save-goal-activate-failed";

export interface SaveGoalCardProps {
  args: SaveGoalArgs;
  /** interactiveToolCall.result；有值时不展示 CTA */
  result?: any;
  toolInfo?: InteractiveToolInfo;
  /** 当前会话 id；Active my plan 成功后用于刷新 chat detail */
  chatId?: string;
  className?: string;
  /** 提交中禁用按钮，避免重复 confirm */
  disabled?: boolean;
  onSubmit?: (payload: SaveGoalCardSubmitPayload) => void | Promise<void>;
}

/**
 * save_goal Activate goal 卡片（Figma 96138:33273 · UI14）。
 */
export const SaveGoalCard: React.FC<SaveGoalCardProps> = ({
  args,
  result,
  toolInfo,
  chatId,
  className,
  disabled = false,
  onSubmit,
}) => {
  const hasResult = hasSaveGoalResult(result);
  const { runActivate, isActivating, navigateToGoal } =
    useGoalActivatePaywallGate();
  const { data: fitnessScore } = useFitnessScore();

  const showGoalImpact = args.impact != null && args.impact.delta > 0;

  const moves = useMemo(
    () => normalizeSaveGoalTrackerSpecs(args.trackerSpecs),
    [args.trackerSpecs],
  );
  const hasMoves = moves.length > 0;
  const isBusy = disabled || isActivating;
  const showInteractiveActions = !hasResult;
  const isCardClickable = hasResult && hasMoves;

  useEffect(() => {
    if (!showInteractiveActions || !hasMoves) {
      return;
    }
    const onboardingDraftId = getOnboardingV3Session()?.planPreview?.draftId;
    const isOnboardingGiftHandoff =
      Boolean(onboardingDraftId) &&
      (!args.goalId || args.goalId === onboardingDraftId);
    if (isOnboardingGiftHandoff) {
      trackGiftGoalSetupDone();
      return;
    }
    trackPlanSetGoalDone();
  }, [args.goalId, hasMoves, showInteractiveActions]);

  const showActivateFailedToast = useMemoizedFn(() => {
    OspToast.error({
      content: ACTIVATE_FAILED_TOAST,
      key: ACTIVATE_FAILED_TOAST_KEY,
    });
  });

  const handleActivatePlan = useMemoizedFn(async () => {
    if (isBusy) {
      return;
    }

    trackPutEdToWorkTap("put_ed_to_work");

    const resolvedGoalId = resolveGoalIdForActivate(args);

    try {
      const activateResult = await runActivate({
        saveGoalArgs: args,
        goalId: resolvedGoalId,
        chatId,
      });

      if (activateResult.outcome === "dismissed") {
        return;
      }

      if (activateResult.outcome === "error") {
        Track.log("error", "SaveGoalCard", "activate flow failed", {
          cause: activateResult.reason,
        });
        showActivateFailedToast();
        return;
      }

      if (activateResult.outcome === "retry_required") {
        Track.log("warn", "SaveGoalCard", "activation retry required", {
          goalId: activateResult.goalId,
          correlationId: activateResult.correlationId,
        });
        showActivateFailedToast();
        return;
      }

      // ACTIVE：统一 append 工具响应，让 Ed 在 chat 流承接。
      // type=activated（对齐 KMP buildSaveGoalActivatedToolResult）：明确告知 agent
      // goal 已创建并激活成功，而非旧 confirm-save 时代含糊的 "confirmed"。
      closePlanUnlockedMovesModal();
      // 起步清单项 6：chat 中立起 goal 成功（PRD S1.11 · goal_create_success）
      trackGoalCreateSuccess();
      try {
        await onSubmit?.({
          type: "activated",
          goalId: activateResult.goalId,
          status: "ACTIVE",
          message: SAVE_GOAL_ACTIVATED_TOOL_MESSAGE,
        });
      } catch (error) {
        Track.log(
          "error",
          "SaveGoalCard",
          "activated goal tool result sync failed",
          {
            goalId: activateResult.goalId,
            cause: String(safeStringifyError(error)),
          },
        );
      } finally {
        navigateToGoal(activateResult.goalId, "chat");
      }
    } catch (error) {
      Track.log("error", "SaveGoalCard", "activate flow failed", {
        cause: String(safeStringifyError(error)),
      });
      showActivateFailedToast();
    }
  });

  const openReadonlyModal = useCallback(() => {
    if (isBusy || !hasMoves) {
      return;
    }

    void openPlanUnlockedMovesModal({
      args,
      mode: "readonly",
    });
  }, [args, hasMoves, isBusy]);

  const openInteractiveModal = useCallback(() => {
    if (isBusy || !hasMoves) {
      return;
    }

    void openPlanUnlockedMovesModal({
      args,
      mode: "interactive",
      isActivating,
      onActivate: handleActivatePlan,
    });
  }, [args, handleActivatePlan, hasMoves, isBusy, isActivating]);

  const handleCardClick = () => {
    if (!isCardClickable || isBusy) {
      return;
    }
    openReadonlyModal();
  };

  return (
    <div
      className={cn(
        "relative flex w-full max-w-[361px] flex-col overflow-hidden rounded-xl border border-border_inverse_transparent10 bg-background_solid_neutral_primary",
        isCardClickable && "cursor-pointer",
        className,
      )}
      data-testid="save-goal-card"
      data-tool-name={toolInfo?.toolName}
      data-tool-call-id={toolInfo?.toolCallId}
      onClick={isCardClickable ? handleCardClick : undefined}
    >
      <SaveGoalCardGoalSection args={args} layout="activate" />

      <div
        className="relative z-[1] flex flex-col gap-6 bg-background_solid_neutral_primary p-5"
        data-testid="save-goal-card-body"
      >
        {showGoalImpact ? (
          <GoalConfirmScoreImpact
            impact={args.impact}
            fitnessScore={fitnessScore ?? null}
            showRadar={Boolean(fitnessScore)}
          />
        ) : null}

        {hasMoves ? (
          <SaveGoalCardPlanPreviewSection
            moves={moves}
            readOnly={hasResult}
            onViewDetails={
              showInteractiveActions ? openInteractiveModal : undefined
            }
          />
        ) : null}

        {showInteractiveActions ? (
          <OnboardingEmphasisCtaButton
            testId="save-goal-card-activate-cta"
            label={SAVE_GOAL_ACTIVATE_COPY.activateCta}
            disabled={isBusy}
            loading={isActivating}
            sizeClassName="button_m"
            className="rounded-lg"
            onClick={(event) => {
              event.stopPropagation();
              void handleActivatePlan();
            }}
          />
        ) : null}
      </div>
    </div>
  );
};
